import { getAuth } from 'firebase/auth';
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';

/**
 * Taille maximale acceptée par upload, ALIGNÉE sur les Security Rules du
 * bucket `skipcar-app.appspot.com` (`request.resource.size < 15 * 1024 * 1024`
 * sur `images/**`, `images/form_cgia/**` et `ocr/**`).
 *
 * Cette constante doit rester synchronisée avec les règles Firebase : c'est le
 * serveur qui tranche, on ne fait ici qu'anticiper son refus pour pouvoir
 * l'expliquer à l'agence.
 */
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

/**
 * Types MIME acceptés par les Security Rules du bucket. Un fichier d'un autre
 * type (TIFF, WebP, DOCX, ou `File.type` vide → `application/octet-stream`)
 * est rejeté par Firebase EXACTEMENT comme un fichier trop lourd, avec le même
 * `storage/unauthorized` indéchiffrable.
 */
const ALLOWED_UPLOAD_MIME_TYPES = ['image/png', 'image/jpeg', 'image/heic', 'image/heif', 'application/pdf'];

/** Formatage des tailles pour les messages destinés aux agences (« 23,6 Mo »). */
export const formatMegabytes = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;

/**
 * Erreur d'upload dont le message est DESTINÉ À L'AGENCE (pas un message
 * technique). Les appelants peuvent l'afficher tel quel.
 *
 * Motivation (dossier FW-262-LA, 2026-08) : Firebase Storage refuse tout
 * fichier violant ses Security Rules avec `storage/unauthorized`, littéralement
 * « User does not have permission to access … ». Un scan de 21 Mo ressortait
 * donc à l'écran en « Le fichier n'a pas pu être analysé », sans jamais dire
 * que le fichier était trop lourd. L'agence redéposait le même fichier en
 * boucle. 28 occurrences sur 14 jours dans Sentry, 8 agences concernées.
 */
export class UploadRejectedError extends Error {
  constructor(message: string, public readonly fileName: string) {
    super(message);
    this.name = 'UploadRejectedError';
  }
}

/**
 * Vérifie qu'un fichier satisfait les Security Rules AVANT de lancer l'upload.
 * Retourne le message à afficher à l'agence, ou `null` si le fichier passe.
 *
 * Exporté pour que les zones de dépôt puissent filtrer à la sélection, sans
 * téléverser 21 Mo pour rien.
 */
export const getUploadRejectionReason = (file: File): string | null => {
  // `>=` et non `>` : la règle Firebase exige `size < 15 Mo`, donc un fichier
  // pesant EXACTEMENT la limite est refusé côté serveur.
  if (file.size >= MAX_UPLOAD_BYTES) {
    return (
      `Fichier trop volumineux (${formatMegabytes(file.size)}, maximum ${formatMegabytes(MAX_UPLOAD_BYTES)}). ` +
      `Rescannez le document en qualité standard (200 à 300 dpi) ou photographiez-le, puis redéposez-le.`
    );
  }
  // `File.type` vide arrive sur certains navigateurs / fichiers sans extension
  // connue : Firebase reçoit alors `application/octet-stream` et refuse.
  if (!ALLOWED_UPLOAD_MIME_TYPES.includes(file.type)) {
    return (
      `Format de fichier non accepté${file.type ? ` (${file.type})` : ''}. ` +
      `Déposez le document en PDF, JPEG, PNG ou HEIC.`
    );
  }
  return null;
};

/**
 * Helper d'upload Firebase Storage avec nom de fichier aléatoire.
 * Utilisé par FormCGIA v2 MultiDropzone et réutilisable ailleurs.
 *
 * Lève une `UploadRejectedError` (message destiné à l'agence) quand le fichier
 * ne satisfait pas les Security Rules, plutôt que de laisser Firebase répondre
 * un `storage/unauthorized` opaque.
 */
export const uploadFileToFirebase = async (
  file: File,
  pathPrefix: string,
  onProgress?: (percent: number) => void,
): Promise<string> => {
  const rejection = getUploadRejectionReason(file);
  if (rejection) {
    throw new UploadRejectedError(rejection, file.name);
  }

  const storage = getStorage();
  const auth = getAuth();

  const uniqueId = Date.now().toString(36) + Math.random().toString(36).substring(2, 7);
  const fileExtension = file.name.split('.').pop() || 'bin';
  const sanitizedName = `${uniqueId}.${fileExtension}`;
  const uid = auth.currentUser?.uid ?? 'anon';
  const storagePath = `${pathPrefix}/${uid}/${sanitizedName}`;
  const storageRef = ref(storage, storagePath);

  const task = uploadBytesResumable(storageRef, file, { contentType: file.type || undefined });

  return new Promise<string>((resolve, reject) => {
    task.on(
      'state_changed',
      (snapshot) => {
        if (onProgress) {
          const percent = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress(percent);
        }
      },
      (error) => {
        // Filet de sécurité : si les Security Rules divergent un jour de
        // `MAX_UPLOAD_BYTES` / `ALLOWED_UPLOAD_MIME_TYPES`, le refus revient ici
        // en `storage/unauthorized`. On l'énonce en clair plutôt que de laisser
        // remonter « User does not have permission to access … ».
        if ((error as { code?: string })?.code === 'storage/unauthorized') {
          reject(
            new UploadRejectedError(
              `Ce fichier a été refusé à l'envoi (${formatMegabytes(file.size)}, ${file.type || 'type inconnu'}). ` +
                `Déposez un PDF, JPEG, PNG ou HEIC de moins de ${formatMegabytes(MAX_UPLOAD_BYTES)}.`,
              file.name,
            ),
          );
          return;
        }
        reject(error);
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve(url);
        } catch (err) {
          reject(err);
        }
      },
    );
  });
};
